import { spacingHelper, mediaHelper } from '@property.com.au/ui-kit';
import type { FunctionComponent } from 'react';
import { useState } from 'react';
import styled, { css } from 'styled-components';
import { SearchLandingPage_Content, SearchResult } from '../../types';
import { useStickyHeaderContext } from '../../contexts/StickyHeaderContext/StickyHeaderContext';
import { SearchLandingPageResults } from '../SearchLandingPageResults/SearchLandingPageResults';
import { SearchLandingPageResultsLeadingBanner } from '../SearchLandingPageResultsLeadingBanner/SearchLandingPageResultsLeadingBanner';
import {
  ResultsBannerImage,
  SearchLandingPageResultsBanner,
} from '../SearchLandingPageResultsBanner/SearchLandingPageResultsBanner';
import resultsBannerField from '../../../assets/results-banner-field.svg';
import resultsBannerHouses from '../../../assets/results-banner-houses.svg';
import { WebLink } from '../../../app-kit/components/WebLink/WebLink';
import { PageContainer } from '../../../components/PageContainer';
import { deepOmitTypeName } from '../../../helpers/deepOmitTypeName';
import { mapWebLinkFragmentToWebLinkProps } from '../../../helpers/mapWebLinkFragmentToWebLinkProps';
import { ObservedElement } from '../../../app-kit/components/ObservedElement/ObservedElement';
import { Pagination } from '../../../components/Pagination/Pagination';
import { SearchLandingPageSuburbInsightsBanner } from '../SearchPageSuburbInsightsBanner/SearchLandingPageSuburbInsightsBanner';
import { mapDynamicContentItems } from '../../../app-kit/components/DynamicContent/helpers/mapDynamicContentItem';

interface SearchLandingPageListProps {
  content: SearchLandingPage_Content;
}

const SearchPageListContainer = styled(PageContainer)`
  margin-top: ${spacingHelper('large')};
  margin-bottom: ${spacingHelper('large')};

  ${mediaHelper('tabletMax')} {
    padding-left: ${spacingHelper('extraSmall')};
    padding-right: ${spacingHelper('extraSmall')};
  }
`;

const Results = styled.div`
  display: flex;
  flex-wrap: wrap;
  justify-content: space-between;

  /* Ensure the SearchByImage button, excluding first, in PropertyCard renders similar to compact variant */
  .search-page-property-card:nth-child(n + 2) {
    .searchByImageBtn {
      padding-left: 0;
      padding-right: 0;

      span {
        display: none;
      }
    }
  }
`;

// We preload images on se arch to help with LCP
// Currently preload 2 sea rch images - across surrounding and exact results
// Also preload the image  for SearchPageResultsBanner (if present) as its likely to also affect our score
const PRELOAD_SEARCH_RESULT_IMAGES_COUNT = 2;

// Position index at which the suburb insights banner is inserted among exact results
const SUBURB_INSIGHTS_BANNER_POSITION = 10;

export const calculateSurroundingItemsPreloadCount = (exactResults: SearchResult[]): number =>
  Math.max(0, PRELOAD_SEARCH_RESULT_IMAGES_COUNT - exactResults.length);

const containsSurroundingSuburbsBannerImage: ResultsBannerImage = {
  name: resultsBannerHouses,
  alt: 'Search Results page image',
  aspectRatio: '160/53',
};

const noSurroundingSuburbsBannerImage: ResultsBannerImage = {
  name: resultsBannerField,
  alt: 'Search Results page image',
  aspectRatio: '160/43',
};
/*
 * MapViewButton shows  in the bottom of the page, it will be:
 * - RENDERED: only when  it has results(exactResults and surroundingResults) and mapViewButton has data
 * - HIDDEN: when map button on the SearchPageHeader is visible or page pagination start appearing
 */
const ShowMapButton = styled(WebLink)<{
  $showMapViewButtonOnMobile: boolean;
  $showMapViewButtonOnDesktop: boolean;
}>`
  position: fixed;
  left: 50%;
  transform: translate(-50%, 50%);
  z-index: 1;

  bottom: -${spacingHelper('twoExtraLarge')};
  transition: 200ms;
  ${({ $showMapViewButtonOnMobile }) =>
    $showMapViewButtonOnMobile &&
    css`
      ${mediaHelper('tabletMax')} {
        bottom: ${spacingHelper('twoExtraLarge')};
      }
    `}
  ${({ $showMapViewButtonOnDesktop }) =>
    $showMapViewButtonOnDesktop &&
    css`
      ${mediaHelper('desktopMin')} {
        bottom: ${spacingHelper('twoExtraLarge')};
      }
    `}
`;

const ShowMapButtonWrapper = styled.div`
  .map-view-button {
    ${mediaHelper('desktopMin')} {
      display: none;
    }
  }

  .map-split-view-button {
    ${mediaHelper('tabletMax')} {
      display: none;
    }
  }
`;

const SearchPagePaginationContainer = styled(PageContainer)`
  margin-top: ${spacingHelper('extraLarge')};
`;

export const SearchLandingPageList: FunctionComponent<SearchLandingPageListProps> = ({
  content,
}) => {
  const {
    exactResults,
    surroundingResults,
    leadingResultsBanner,
    resultsBannerV2,
    mapViewButtonV2,
    mapSplitViewButton,
    pagination,
    suburbInsightsBanner,
  } = content;
  const mapViewButtonProps = mapViewButtonV2;
  const [isSearchPagePaginationVisible, setIsSearchPagePaginationVisible] = useState(false);
  const surroundingItemsPreloadCount = calculateSurroundingItemsPreloadCount(exactResults);
  const { stickyState } = useStickyHeaderContext();
  const isHeaderMapButtonNotShowOnMobile = stickyState === 'header-collapsed';
  const isHeaderMapButtonNotShowOnDesktop =
    stickyState === 'header-collapsed' || stickyState === 'header-partial-reveal';
  const bannerImage = surroundingResults?.length
    ? containsSurroundingSuburbsBannerImage
    : noSurroundingSuburbsBannerImage;
  const onIntersectChange = (isIntersecting: boolean) =>
    setIsSearchPagePaginationVisible(isIntersecting);
  const shouldRenderShowMapButtonAtPageBottom = exactResults.length || surroundingResults?.length;
  const showShowMapButtonAtPageBottomOnMobile =
    !isSearchPagePaginationVisible && isHeaderMapButtonNotShowOnMobile;
  const showShowMapButtonAtPageBottomOnDesktop =
    !isSearchPagePaginationVisible && isHeaderMapButtonNotShowOnDesktop;

  const exactResultsBeforeBanner = exactResults.slice(0, SUBURB_INSIGHTS_BANNER_POSITION);
  const exactResultsAfterBanner = exactResults.slice(SUBURB_INSIGHTS_BANNER_POSITION);
  const showSuburbInsightsBanner = suburbInsightsBanner && exactResults.length > 0;

  return (
    <SearchPageListContainer>
      <Results>
        <SearchLandingPageResults
          results={exactResultsBeforeBanner}
          preloadItemCount={PRELOAD_SEARCH_RESULT_IMAGES_COUNT}
        />
        {showSuburbInsightsBanner && (
          <SearchLandingPageSuburbInsightsBanner
            title={suburbInsightsBanner.title}
            insights={suburbInsightsBanner.insights.map(mapDynamicContentItems)}
            cta={mapWebLinkFragmentToWebLinkProps(suburbInsightsBanner.cta)}
            disclaimerText={suburbInsightsBanner.disclaimerText}
          />
        )}

        {exactResultsAfterBanner.length > 0 && (
          <SearchLandingPageResults results={exactResultsAfterBanner} preloadItemCount={0} />
        )}
        {leadingResultsBanner && (
          <SearchLandingPageResultsLeadingBanner {...deepOmitTypeName(leadingResultsBanner)} />
        )}
        {resultsBannerV2 && (
          <SearchLandingPageResultsBanner {...resultsBannerV2} image={bannerImage} />
        )}
        <SearchLandingPageResults
          results={surroundingResults ?? []}
          preloadItemCount={surroundingItemsPreloadCount}
        />
      </Results>
      <ObservedElement onIntersectChange={onIntersectChange}>
        {pagination && (
          <SearchPagePaginationContainer>
            <Pagination pagination={pagination} />
          </SearchPagePaginationContainer>
        )}
      </ObservedElement>
      {shouldRenderShowMapButtonAtPageBottom ? (
        <ShowMapButtonWrapper>
          {mapViewButtonProps && (
            <ShowMapButton
              {...mapWebLinkFragmentToWebLinkProps(mapViewButtonProps)}
              $showMapViewButtonOnMobile={showShowMapButtonAtPageBottomOnMobile}
              $showMapViewButtonOnDesktop={showShowMapButtonAtPageBottomOnDesktop}
              className="map-view-button"
            />
          )}
          {mapSplitViewButton && (
            <ShowMapButton
              {...mapWebLinkFragmentToWebLinkProps(mapSplitViewButton)}
              $showMapViewButtonOnMobile={showShowMapButtonAtPageBottomOnMobile}
              $showMapViewButtonOnDesktop={showShowMapButtonAtPageBottomOnDesktop}
              className="map-split-view-button"
            />
          )}
        </ShowMapButtonWrapper>
      ) : null}
    </SearchPageListContainer>
  );
};
